import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Globe, AlertCircle, CheckCircle, Lock, Package, Users, Shield } from 'lucide-react';

const SSODomainManagement = ({ organizationId, organizationName, currentUser }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ssoLockedByPackage, setSsoLockedByPackage] = useState(false);
  const [activePackageName, setActivePackageName] = useState('');

  // Non-corporate email SSO settings
  const [allowAnyDomainSSO, setAllowAnyDomainSSO] = useState(false);
  const [requireInviteForSSO, setRequireInviteForSSO] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Check if user has permission to manage domains
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isOrgAdmin = currentUser?.is_organization_admin === true && currentUser?.organization_id === organizationId;
  const canManage = isSuperAdmin || isOrgAdmin;

  useEffect(() => {
    fetchDomains();
    fetchOrgSettings();
  }, [organizationId]);

  const fetchDomains = async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`/api/organizations/${organizationId}/sso-domains`);
      setDomains(response.data.sso_domains || []);
      setSsoLockedByPackage(response.data.sso_locked_by_package || false);
      setActivePackageName(response.data.active_package_name || '');
    } catch (err) {
      console.error('Error fetching SSO domains:', err);
      setError('Failed to load SSO domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgSettings = async () => {
    if (!canManage) return;

    try {
      const response = await axios.get(`/api/organizations/${organizationId}`);
      setAllowAnyDomainSSO(response.data.allow_any_domain_sso || false);
      setRequireInviteForSSO(response.data.require_invite_for_sso !== false); // Default true
    } catch (err) {
      console.error('Error fetching org settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    setSuccess('');

    try {
      await axios.patch(`/api/organizations/${organizationId}`, {
        allow_any_domain_sso: allowAnyDomainSSO,
        require_invite_for_sso: requireInviteForSSO
      });
      setSuccess('SSO settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving SSO settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setError('');
    setSuccess('');
    setAdding(true);

    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/sso-domains`,
        null,
        { params: { domain: newDomain.trim().toLowerCase() } }
      );

      setDomains(response.data.sso_domains || []);
      setNewDomain('');
      setSuccess(`Domain '${newDomain}' added successfully!`);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding SSO domain:', err);
      setError(err.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove '${domain}' from SSO domains?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(
        `/api/organizations/${organizationId}/sso-domains/${encodeURIComponent(domain)}`
      );

      setDomains(response.data.sso_domains || []);
      setSuccess(`Domain '${domain}' removed successfully!`);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error removing SSO domain:', err);
      setError(err.response?.data?.detail || 'Failed to remove domain');
    }
  };

  if (!canManage) {
    return (
      <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
        <p className="text-ui-amber-fg text-sm">
          You don't have permission to manage SSO domains for this organization.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-ui-ink flex items-center gap-2">
            <Globe className="w-5 h-5 text-ui-link" />
            Google SSO Domains
          </h3>
          <p className="text-sm text-ui-ink-3 mt-1">
            Users with email addresses from these domains can sign in with Google SSO
          </p>
        </div>
      </div>

      {/* SSO Locked by Package Notice */}
      {ssoLockedByPackage && (
        <div className="bg-ui-amber-bg border border-ui-amber-line rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 bg-ui-amber-bg rounded-full">
                <Lock className="h-5 w-5 text-ui-amber-fg" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-ui-amber-fg mb-1">
                SSO Not Available in Current Package
              </h4>
              <p className="text-sm text-ui-amber-fg mb-2">
                SSO domain management is not included in the assigned package:
              </p>
              <div className="flex items-center space-x-2 bg-ui-amber-bg rounded-md px-3 py-2 inline-flex">
                <Package className="h-4 w-4 text-ui-amber-fg" />
                <span className="font-medium text-ui-amber-fg">{activePackageName || 'Unknown Package'}</span>
              </div>
              <p className="text-xs text-ui-amber-fg mt-2">
                Contact your administrator to upgrade to a package that includes SSO.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="bg-ui-green-bg border border-ui-green-line rounded-lg p-3 flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-ui-green-fg flex-shrink-0 mt-0.5" />
          <p className="text-ui-green-fg text-sm">{success}</p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-ui-red-bg border border-ui-red-line rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-ui-red-fg flex-shrink-0 mt-0.5" />
          <p className="text-ui-red-fg text-sm">{error}</p>
        </div>
      )}

      {/* Add domain form */}
      {!ssoLockedByPackage && (
        <>
          <form onSubmit={handleAddDomain} className="flex gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="flex-1 px-4 py-2 border border-ui-control rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
              disabled={adding}
            />
            <button
              type="submit"
              disabled={adding || !newDomain.trim()}
              className="px-4 py-2 bg-ui-primary text-ui-on-primary rounded-lg hover:bg-ui-primary-hover disabled:bg-ui-control disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              {adding ? 'Adding...' : 'Add Domain'}
            </button>
          </form>

          <div className="text-xs text-ui-muted bg-ui-sunk rounded p-2">
            <strong>Note:</strong> Enter only the domain (e.g., "company.com"), not full email addresses.
          </div>
        </>
      )}

      {/* Domain list */}
      <div className={`border border-ui-line rounded-lg divide-y divide-ui-line ${ssoLockedByPackage ? 'opacity-60' : ''}`}>
        {domains.length === 0 ? (
          <div className="p-6 text-center text-ui-muted">
            <Globe className="w-12 h-12 text-ui-faint mx-auto mb-2" />
            <p>No SSO domains configured</p>
            {!ssoLockedByPackage && (
              <p className="text-sm mt-1">Add a domain above to enable Google SSO for this organization</p>
            )}
          </div>
        ) : (
          domains.map((domain) => (
            <div
              key={domain}
              className="flex items-center justify-between p-4 hover:bg-ui-hover transition-colors"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-ui-muted" />
                <span className="font-medium text-ui-ink">{domain}</span>
                <span className="text-xs text-ui-muted bg-ui-green-bg px-2 py-0.5 rounded-full">
                  Active
                </span>
              </div>
              {!ssoLockedByPackage && (
                <button
                  onClick={() => handleRemoveDomain(domain)}
                  className="p-2 text-ui-red-fg hover:bg-ui-red-bg rounded-lg transition-colors"
                  title="Remove domain"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {domains.length > 0 && !ssoLockedByPackage && (
        <div className="text-sm text-ui-ink-3 bg-ui-blue-bg border border-ui-blue-line rounded-lg p-3">
          <strong className="text-ui-blue-fg">How it works:</strong>
          <ul className="mt-2 space-y-1 list-disc list-inside text-ui-blue-fg">
            <li>Users with emails from these domains can click "Sign in with Google" on the login page</li>
            <li>New users will be automatically created when they first sign in</li>
            <li>Existing users will have their account linked to Google SSO</li>
          </ul>
        </div>
      )}

      {/* Non-Corporate Email SSO Settings */}
      {!ssoLockedByPackage && (
        <div className="mt-8 border-t border-ui-line pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-ui-ink flex items-center gap-2">
                <Users className="w-5 h-5 text-ui-violet-fg" />
                Guest & Contractor SSO Settings
              </h3>
              <p className="text-sm text-ui-ink-3 mt-1">
                Allow users with non-corporate email addresses (e.g., gmail.com) to sign in via SSO
              </p>
            </div>
          </div>

          <div className="bg-ui-sunk border border-ui-line rounded-lg p-4 space-y-4">
            {/* Allow Any Domain SSO Toggle */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="flex items-center gap-2 text-sm font-medium text-ui-ink">
                  <Shield className="w-4 h-4 text-ui-muted" />
                  Allow non-corporate email SSO
                </label>
                <p className="text-xs text-ui-muted mt-1 ml-6">
                  When enabled, invited users with any email domain (e.g., gmail.com, outlook.com)
                  can sign in using Google or Microsoft SSO instead of password.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={allowAnyDomainSSO}
                  onChange={(e) => setAllowAnyDomainSSO(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-ui-line peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ui-violet-line rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-ui-surface after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-ui-surface after:border-ui-control after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ui-violet-strong"></div>
              </label>
            </div>

            {/* Require Invite Toggle (only shown when allow any domain is enabled) */}
            {allowAnyDomainSSO && (
              <div className="flex items-start justify-between pl-6 border-l-2 border-ui-violet-line">
                <div className="flex-1">
                  <label className="flex items-center gap-2 text-sm font-medium text-ui-ink">
                    <Lock className="w-4 h-4 text-ui-muted" />
                    Require invite for non-corporate emails
                  </label>
                  <p className="text-xs text-ui-muted mt-1 ml-6">
                    When enabled, users with non-corporate emails must be invited before they can sign in via SSO.
                    This prevents unauthorized access from anyone with a personal email.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={requireInviteForSSO}
                    onChange={(e) => setRequireInviteForSSO(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-ui-line peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ui-violet-line rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-ui-surface after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-ui-surface after:border-ui-control after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ui-violet-strong"></div>
                </label>
              </div>
            )}

            {/* Info box about the feature */}
            {allowAnyDomainSSO && (
              <div className="bg-ui-violet-bg border border-ui-violet-line rounded-lg p-3 mt-4">
                <p className="text-sm text-ui-violet-fg">
                  <strong>How it works:</strong> Contractors and guests with personal email addresses
                  (like gmail.com) can now use Google or Microsoft SSO to sign in, as long as they've
                  been invited to your organization. They'll be able to choose which organization to
                  sign into if they belong to multiple.
                </p>
              </div>
            )}

            {/* Save button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-4 py-2 bg-ui-violet-strong text-ui-on-primary rounded-lg hover:bg-ui-violet-strong/90 disabled:bg-ui-control disabled:cursor-not-allowed font-medium flex items-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-surface"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSODomainManagement;
